import useGetTokenProfile from "@/hooks/useGetTokenProfile";
import { differenceInCalendarDays } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const CARD_GRADIENT = "linear-gradient(to right, #f8f7ff 0%, #f5f4ff 100%)";
const PROGRESS_TRACK_BG = "#EDE9FE";
const PROGRESS_FILL_GRADIENT =
    "linear-gradient(to right, #4B0082 0%, #8A2BE2 100%)";

const TokenConsumptionCard = () => {
    const { t } = useTranslation();
    const { data: tokenProfile } = useGetTokenProfile();

    const renewalLabel = useMemo(() => {
        const renewalDate = tokenProfile?.renewalDate;
        if (!renewalDate) {
            return null;
        }

        const daysLeft = differenceInCalendarDays(renewalDate, new Date());
        if (daysLeft <= 1) {
            return t("settings.account.tokens.renewalTomorrow");
        }

        return t("settings.account.tokens.renewalInDays", { count: daysLeft });
    }, [tokenProfile?.renewalDate, t]);

    if (!tokenProfile) {
        return null;
    }

    const { used, total, usagePercent } = tokenProfile;

    return (
        <div className="w-1/2">
            <h2 className="mb-2 text-sm font-medium uppercase text-text-caption">
                {t("settings.account.tokens.title")}
            </h2>
            <div
                className="flex flex-col gap-4 rounded-xl p-4 shadow-sm"
                style={{ background: CARD_GRADIENT }}
            >
                <div className="flex items-start justify-between">
                    <div className="flex flex-col gap-1">
                        <p className="text-xs text-text-subtitle">
                            {t("settings.account.tokens.used")}
                        </p>
                        <p className="text-text-title text-2xl font-bold">
                            {used}
                        </p>
                    </div>

                    <div className="flex h-16 w-16 flex-shrink-0 flex-col items-center justify-center rounded-full bg-white shadow-md">
                        <span className="bg-gradient-to-r from-indigo-700 to-violet-500 bg-clip-text text-2xl font-bold leading-none text-transparent">
                            {usagePercent}
                        </span>
                        <span className="text-xs font-medium text-slate-500">
                            %
                        </span>
                    </div>
                </div>

                <div
                    className="h-1.5 w-full overflow-hidden rounded-full"
                    style={{ backgroundColor: PROGRESS_TRACK_BG }}
                >
                    <div
                        className="h-full min-w-0 rounded-full transition-all"
                        style={{
                            width: `${usagePercent}%`,
                            background: PROGRESS_FILL_GRADIENT,
                        }}
                    />
                </div>

                <div className="flex items-center justify-between text-xs text-text-subtitle">
                    <span>{t("settings.account.tokens.monthlyLimit")}</span>
                    <span className="text-text-title font-semibold">
                        {total} {t("settings.account.tokens.unit")}
                    </span>
                </div>

                {renewalLabel && (
                    <div className="border-t border-violet-200/50 pt-3">
                        <p className="text-xs text-text-subtitle">
                            {renewalLabel}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TokenConsumptionCard;
